import React from 'react';
import RGL from 'react-grid-layout';
import { Controller, useForm } from 'react-hook-form';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useCameras } from 'cameras/hooks/useCameras';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useDeployments } from 'deployments/hooks/useDeployments';
import { useGateways } from 'gateways/hooks/useGateways';
import { useModal } from 'hooks/useModal';
import { usePipelines } from 'pipelines/hooks/usePipelines';
import { useStreams } from 'streams/hooks/useStreams';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import { FilesWidget } from 'types/dashboard_widget';
import { UseFilesParams } from 'files/hooks/useFiles';

import { Button } from 'components/Button/Button';
import { CameraSelect } from 'cameras/components/CameraSelect/CameraSelect';
import { DeploymentSelect } from 'deployments/components/DeploymentSelect/DeploymentSelect';
import { Field } from 'components/Field/Field';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { Input } from 'components/Input/Input';
import { Loader } from 'components/Loader/Loader';
import { MultiSelectInput } from 'components/MultiSelectInput/MultiSelectInput';
import { PipelineSelect } from 'pipelines/components/PipelineSelect/PipelineSelect';
import { Select } from 'components/Select/Select';
import { StreamSelect } from 'streams/components/StreamSelect/StreamSelect';
import { TagSelect } from 'tags/components/TagSelect/TagSelect';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

const FILTER_OPTIONS = [
  { label: 'Cameras', value: 'camera_ids' },
  { label: 'Workflow deployments', value: 'deployment_ids' },
  { label: 'Gateways', value: 'gateway_ids' },
  { label: 'Labels', value: 'tagged_with' },
  { label: 'Node IDs', value: 'node_ids' },
  { label: 'Workflows', value: 'pipeline_ids' },
  { label: 'Streams', value: 'stream_ids' },
] as const;

type FilesWidgetFieldValues = {
  name: string;
  camera_ids: Array<string>;
  deployment_ids: Array<string>;
  gateway_ids: Array<string>;
  tagged_with: Array<string>;
  node_ids: Array<string>;
  pipeline_ids: Array<string>;
  stream_ids: Array<string>;
};

type FilesFormProps = { widget?: FilesWidget; dashboardId: string };

export function FilesForm({ widget, dashboardId }: FilesFormProps) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const options = widget ? widget.options : {};

  const {
    camera_ids = [],
    deployment_ids = [],
    gateway_ids = [],
    node_ids = [],
    pipeline_ids = [],
    stream_ids = [],
    tagged_with = [],
  } = options;

  const { data: initialPipelines, isInitialLoading: isPipelinesLoading } =
    usePipelines({
      params: {
        pipeline_ids,
        limit: pipeline_ids.length,
      },
      enabled: pipeline_ids.length > 0,
    });

  const { data: initialGateways, isInitialLoading: isGatewaysLoading } =
    useGateways({
      params: {
        gateway_ids,
        limit: gateway_ids.length,
      },
      enabled: gateway_ids.length > 0,
    });

  const { data: initialDeployments, isInitialLoading: isDeploymentsLoading } =
    useDeployments({
      params: {
        deployment_ids: deployment_ids,
        limit: deployment_ids.length,
      },
      enabled: deployment_ids.length > 0,
      keepPreviousData: true,
    });

  const { data: initialStreams, isInitialLoading: isStreamsLoading } =
    useStreams({
      params: {
        stream_ids: stream_ids,
        limit: stream_ids.length,
      },
      enabled: stream_ids.length > 0,
    });

  const { data: initialCameras, isInitialLoading: isCamerasLoading } =
    useCameras({
      params: {
        camera_ids,
        limit: camera_ids.length,
      },
      enabled: camera_ids.length > 0,
    });

  const isLoading =
    isPipelinesLoading ||
    isGatewaysLoading ||
    isDeploymentsLoading ||
    isStreamsLoading ||
    isCamerasLoading;

  const { mutate, isLoading: isUpdating } = useUpdateDashboard({
    onSuccess: () => close(),
  });

  const {
    formState: { errors, dirtyFields },
    control,
    handleSubmit,
    setValue,
    register,
  } = useForm<FilesWidgetFieldValues>({
    defaultValues: {
      name: widget?.name,
      camera_ids,
      deployment_ids,
      gateway_ids,
      node_ids,
      pipeline_ids,
      stream_ids,
      tagged_with,
    },
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  const [filters, setFilters] = React.useState<
    Array<{
      entityType:
        | 'camera_ids'
        | 'deployment_ids'
        | 'gateway_ids'
        | 'node_ids'
        | 'pipeline_ids'
        | 'stream_ids'
        | 'tagged_with'
        | 'undecided';
    }>
  >(() => {
    const initialFilters = [];

    if (camera_ids.length > 0) {
      initialFilters.push({
        entityType: 'camera_ids' as const,
      });
    }

    if (deployment_ids.length > 0) {
      initialFilters.push({
        entityType: 'deployment_ids' as const,
      });
    }

    if (gateway_ids.length > 0) {
      initialFilters.push({
        entityType: 'gateway_ids' as const,
      });
    }

    if (node_ids.length > 0) {
      initialFilters.push({
        entityType: 'node_ids' as const,
      });
    }

    if (pipeline_ids.length > 0) {
      initialFilters.push({
        entityType: 'pipeline_ids' as const,
      });
    }

    if (stream_ids.length > 0) {
      initialFilters.push({
        entityType: 'stream_ids' as const,
      });
    }

    if (tagged_with.length > 0) {
      initialFilters.push({
        entityType: 'tagged_with' as const,
      });
    }

    return initialFilters;
  });

  function onSubmit({
    name,
    camera_ids,
    deployment_ids,
    gateway_ids,
    tagged_with,
    node_ids,
    pipeline_ids,
    stream_ids,
  }: FilesWidgetFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget?.id || crypto.randomUUID();
    const layout: Omit<RGL.Layout, 'i'> =
      widget?.layout ||
      getNewWidgetLayout(dashboard.configuration.widgets, 4, 5);

    const options: UseFilesParams = {
      camera_ids,
      deployment_ids,
      gateway_ids,
      node_ids,
      pipeline_ids,
      stream_ids,
      tagged_with,
    };

    const updatedDashboard = upsertWidget(
      dashboard,
      new FilesWidget(id, name, options, layout)
    );

    mutate({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  function getAvailableOptions() {
    return FILTER_OPTIONS.filter(
      (option) =>
        !new Set(
          filters
            .filter((filter) => filter.entityType)
            .map((filter) => filter.entityType)
        ).has(option.value)
    );
  }

  const canAdd = filters.length < FILTER_OPTIONS.length;

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="files" />
      <Field label="Widget name" error={errors.name}>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
        />
      </Field>
      <Field
        label="Filters"
        error={
          errors.camera_ids ||
          errors.deployment_ids ||
          errors.gateway_ids ||
          errors.node_ids ||
          errors.pipeline_ids ||
          errors.tagged_with ||
          errors.stream_ids
        }
      >
        {isLoading ? (
          <Loader size="small" text="Loading filters" />
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {filters.map((filter, index) => (
                <div
                  className="grid grid-cols-[1fr_3fr_auto] items-center gap-2"
                  key={filter.entityType}
                >
                  <div>
                    <Select
                      aria-label="Select type"
                      options={getAvailableOptions()}
                      value={FILTER_OPTIONS.find(
                        ({ value }) => value === filter.entityType
                      )}
                      onChange={(option) => {
                        if (!option) return;

                        setFilters((previousFilters) =>
                          previousFilters.map((filter, i) => {
                            if (i === index) {
                              if (filter.entityType !== 'undecided') {
                                setValue(filter.entityType, []);
                              }
                              return { entityType: option.value };
                            }
                            return filter;
                          })
                        );
                      }}
                    />
                  </div>
                  <div>
                    {filter.entityType !== 'undecided' ? (
                      <Controller
                        control={control}
                        name={filter.entityType}
                        render={({ field: { onChange } }) => {
                          switch (filter.entityType) {
                            case 'camera_ids':
                              return (
                                <CameraSelect
                                  defaultValue={initialCameras?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((cameras) => cameras.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'deployment_ids':
                              return (
                                <DeploymentSelect
                                  defaultValue={initialDeployments?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((deployment) => deployment.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'gateway_ids':
                              return (
                                <GatewaySelect
                                  defaultValue={initialGateways?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((gateway) => gateway.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'node_ids':
                              return (
                                <MultiSelectInput
                                  id="node-ids-files-filter"
                                  defaultSelected={node_ids}
                                  onChange={onChange}
                                  allowCreate
                                />
                              );
                            case 'pipeline_ids':
                              return (
                                <PipelineSelect
                                  defaultValue={initialPipelines?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((pipeline) => pipeline.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'stream_ids':
                              return (
                                <StreamSelect
                                  defaultValue={initialStreams?.data}
                                  queryFilters={{
                                    stream_types: ['webrtc', 'rtsp'],
                                  }}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((deployment) => deployment.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'tagged_with':
                              return (
                                <TagSelect
                                  defaultValue={tagged_with}
                                  onChange={onChange}
                                />
                              );
                            default:
                              return <Select options={[]} isDisabled />;
                          }
                        }}
                        rules={{ required: true }}
                      />
                    ) : (
                      <Select options={[]} isDisabled />
                    )}
                  </div>
                  <IconButton
                    icon="cancel"
                    label="Remove filter"
                    onClick={() => {
                      setFilters((previousFilters) => {
                        const filterToRemove = previousFilters[index];
                        if (filterToRemove.entityType !== 'undecided') {
                          setValue(filterToRemove.entityType, []);
                        }
                        return previousFilters.filter((_, i) => i !== index);
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            <div>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  setFilters((previousFilters) => [
                    ...previousFilters,
                    { entityType: 'undecided' },
                  ]);
                }}
                disabled={
                  !canAdd ||
                  isLoading ||
                  filters.at(-1)?.entityType === 'undecided'
                }
              >
                <Icon name="plus" size="xsmall" />
                <span>Add filter</span>
              </Button>
            </div>
          </>
        )}
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating || isLoading} />
    </form>
  );
}
